import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { formatter, Task } from '@repo/shared';

import { useAggregateClusters } from '@/api/generated';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

interface OverallScoreProps {
  clusterId: string;
}

const OverallScore = ({ clusterId }: OverallScoreProps) => {
  const { t } = useTranslation();
  const { data } = useAggregateClusters({
    clusterIds: [clusterId],
  });

  // const task = useMemo(() => data?.[0]?.dataset.task, [data]);

  const metrics = useMemo(() => data?.[0]?.metrics, [data]);

  if (!metrics) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('overview.overallScore')}</CardTitle>
      </CardHeader>
      <CardContent className='flex flex-row gap-8'>
        <div className='flex flex-col gap-2'>
          <span>{t('metrics.cost')}</span>
          <span>{formatter.metrics.cost(metrics.cost)}</span>
        </div>
        <div className='flex flex-col gap-2'>
          <span>{t('metrics.time')}</span>
          <span>{formatter.metrics.time(metrics.time)}</span>
        </div>

        {metrics.task === Task.TEXT_TRANSLATION && (
          <>
            <div className='flex flex-col gap-2'>
              <span>{t('metrics.bleu')}</span>
              <span>{formatter.metrics.bleu(metrics.bleu)}</span>
            </div>
            <div className='flex flex-col gap-2'>
              <span>{t('metrics.comet')}</span>
              <span>{formatter.metrics.comet(metrics.comet)}</span>
            </div>
          </>
        )}

        {metrics.task === Task.GENERATE_AUDIO && (
          <>
            <div className='flex flex-col gap-2'>
              <span>{t('metrics.wordsPerMinute')}</span>
              <span>{formatter.metrics.bleu(metrics.wordsPerMinute)}</span>
            </div>
            <div className='flex flex-col gap-2'>
              <span>{t('metrics.wordErrorRate')}</span>
              <span>{formatter.metrics.comet(metrics.wordErrorRate)}</span>
            </div>
          </>
        )}

        {metrics.task === Task.EXTRACT_STRUCTURE && (
          <>
            <div className='flex flex-col gap-2'>
              <span>{t('metrics.cer')}</span>
              <span>{formatter.metrics.cer(metrics.cer)}</span>
            </div>
            <div className='flex flex-col gap-2'>
              <span>{t('metrics.wer')}</span>
              <span>{formatter.metrics.wer(metrics.wer)}</span>
            </div>
            <div className='flex flex-col gap-2'>
              <span>{t('metrics.structuralDistance')}</span>
              <span>{formatter.metrics.comet(metrics.structuralDistance)}</span>
            </div>
          </>
        )}

        {metrics.task === Task.DESCRIBE_IMAGE && (
          <>
            <div className='flex flex-col gap-2'>
              <span>{t('metrics.bleu')}</span>
              <span>{formatter.metrics.bleu(metrics.bleu)}</span>
            </div>
            <div className='flex flex-col gap-2'>
              <span>{t('metrics.acuCoverage')}</span>
              <span>{formatter.metrics.acuCoverage(metrics.acuCoverage)}</span>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default OverallScore;
